import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import useApi from '../useApi';
import { useEffect } from 'react';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { mapStowagePlans } from 'src/factory/Stowage/StowagePlanFactory';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import useGetStowageApi from './useGetStowageApi';
import usePreloadBaseStowageData from 'src/hooks/stowage/usePreloadBaseStowageData';
import { updateStowageDataSimple } from 'src/utils/stowage/updateStowageDataUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { getMasterStowagePlan, fillMissingImosSyncDateFromParents } from 'src/utils/stowage/stowagePlanUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData } = useApi();
    const { setLoadedMasterStowagePlan } = useStowagePlanStore();
    const { getStowagePlanChanges } = useGetStowageApi();
    const { processApiEndpointError } = apiEndpointUtils();
    // OPTIMIZATION: Use preloaded data instead of loading synchronously
    // This data was preloaded during app initialization in RouterContent
    const { getPreloadedData } = usePreloadBaseStowageData();

    const getMasterStowagePlanFromApi = async (vessel: Vessel) => {
        const imoNumber = vessel.imoNumber;
        // Get preloaded base stowage data (waits if preload is still in progress)
        // This is much faster than the previous approach since the data was preloaded
        // in parallel with other initialization calls
        const baseDataResult = await getPreloadedData();
        if (!baseDataResult.success) {
            console.error('Failed to load base stowage data:', baseDataResult.error);
            return { success: false, error: baseDataResult.error };
        }

        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getMasterStowagePlan'].url,
                queryParam: buildQueryParams({ imoNumber }),
                additionalInfo: { imoNumber },
            });

            if (!Array.isArray(result) || result.length === 0) {
                console.warn(`No master stowage plan data returned for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan data returned' };
            }

            const bms = result as StowagePlanBM[];
            const _stowagePlans = mapStowagePlans(bms);
            const _masterStowagePlan = getMasterStowagePlan(_stowagePlans);

            if (!_masterStowagePlan) {
                console.warn(`No master stowage plan found for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan found' };
            }

            fillMissingImosSyncDateFromParents(_masterStowagePlan, _stowagePlans);

            // Force cache refresh to ensure latest changes
            const stowagePlanChanges = await getStowagePlanChanges(_masterStowagePlan.stowagePlanGuid, true);
            const { stowagePlanRotationPortCalls, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, _masterStowagePlan);
            await updateStowageDataSimple(
                updatedStowagePlan,
                stowagePlanRotationPortCalls,
                updatedStowagePlan.cargoes,
                baseDataResult.data.products,
                baseDataResult.data.productCategories,
                baseDataResult.data.stowageRuleMaps,
                baseDataResult.data.portNames,
                baseDataResult.data.berthNames,
                vessel
            );
            setLoadedMasterStowagePlan({ ...updatedStowagePlan });

            return { success: true, data: updatedStowagePlan };
        } catch (error) {
            console.error('Error loading master stowage plan:', error);
            return { success: false, error };
        }
    };

    useEffect(() => {
        (async () => {
            if (state.status !== 'success') {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return { getMasterStowagePlanFromApi };
};
